'use client'

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react'

interface User {
  id: string
  name: string
  employeeId: string
  department: string | null
  permissions: string[]
  isSeeded: boolean
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isElevated: boolean
  login: (password: string, employeeId?: string) => Promise<boolean>
  logout: () => void
  enableElevated: () => void
  disableElevated: () => void
  getAuthHeaders: () => Record<string, string>
  getToken: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isElevated, setIsElevated] = useState(false)
  const [token, setToken] = useState<string | null>(null)

  // Load token from localStorage on mount
  useEffect(() => {
    // Only access localStorage on client side
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('auth_token')
      if (storedToken) {
        setToken(storedToken)
        verifyToken(storedToken)
      } else {
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }, [])

  // Save token to localStorage when it changes
  useEffect(() => {
    // Only access localStorage on client side
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('auth_token', token)
      } else {
        localStorage.removeItem('auth_token')
      }
    }
  }, [token])

  const verifyToken = async (authToken: string) => {
    try {
      const response = await fetch('/api/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // CRITICAL: Set token state when verification succeeds
        setToken(authToken)
        setUser(data.operator)
        setIsElevated(data.operator.permissions?.includes('admin') || false)
      } else {
        // Token is invalid, clear it
        setToken(null)
        setUser(null)
        setIsElevated(false)
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      setToken(null)
      setUser(null)
      setIsElevated(false)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (password: string, employeeId?: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, employeeId })
      })

      if (response.ok) {
        const data = await response.json()
        setToken(data.token)
        setUser(data.operator)
        setIsElevated(data.operator.permissions?.includes('admin') || false)
        return true
      }
      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setIsElevated(false)
  }

  const enableElevated = () => {
    if (user) {
      setIsElevated(true)
    }
  }

  const disableElevated = () => {
    setIsElevated(false)
  }

  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (!token) {
      throw new Error('Not authenticated')
    }
    return {
      'Authorization': `Bearer ${token}`
    }
  }, [token])

  const getToken = useCallback((): string | null => {
    return token
  }, [token])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user && !!token,
    isElevated,
    login,
    logout,
    enableElevated,
    disableElevated,
    getAuthHeaders,
    getToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}